<script setup>
import { storeToRefs } from 'pinia'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'
import { useMessageStore } from '~/stores/messages'
import { TRACKING_CONSTANTS } from '@/utils/trackingConstants'

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Fetch Center Details
const { data: centerDetails, isPlaceholderData: isCenterDetailsPlaceholder, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

onServerPrefetch(async () => {
    await centerDetailSuspense()
})

// Center has opening hours
const hasOpeningHours = computed(() => centerDetails.value?.hours?.weekly.length > 0)

// Center has contact items
const hasContacts = computed(() => centerDetails.value?.contacts ? Object.keys(centerDetails.value?.contacts).length > 0 : false)

// Configs
const changeCenterButtonConfig = computed(() => {
    return {
        buttonType: 'primary',
        type: 'div',
        active: true,
        large: true,
        fn: async () => await openCenterSelector(),
        placeholder: isCenterDetailsPlaceholder.value
    }
})

const centerRouteConfig = computed(() => {
    return {
        link: centerDetails?.value?.address.googleMaps,
        address: centerDetails?.value?.address?.plain,
        label: staticKeys.value?.center_route_button_label,
        placeholder: isCenterDetailsPlaceholder.value
    }
})

const getCenterListConfig = computed(() => {
    return {
        items: [{
            name: centerDetails.value?.centerName,
            uid: centerDetails.value?.centerUid,
            logotype: centerDetails.value?.logotype,
            placeholder: isCenterDetailsPlaceholder.value
        }]
    }
})

/**
 *  Open center selector
 *
 * @returns {Promise<void>}
 */
const openCenterSelector = async () => {
    // Navigate and add the 'center-switch' query param, keep existing query, but exclude the params defined in the above array
    await navigateWithSidebarQuery({
        query: {
            'center-switch': null
        },
        route
    })
}

// Track Page Load
trackPageLoad({
    title: TRACKING_CONSTANTS.TITLES.CENTER_DETAILS,
    centerUid: centerDetails.value?.centerUid,
    centerName: centerDetails.value?.centerName
})
</script>

<template>
    <SideBarWrapper class="center-detail-sidebar">
        <SideBarHeader>
            <SideBarMainHeadline
                :sidebar-main-headline-config="{
                    title: staticKeys?.centerselector_center_headline__markdown,
                    placeholder: isCenterDetailsPlaceholder
                }"
            />
        </SideBarHeader>

        <CenterList :center-list-config="getCenterListConfig" />

        <div class="center-selector-button">
            <BaseButton :base-button-config="changeCenterButtonConfig">
                {{ staticKeys?.centerselector_change_btn_label }}
            </BaseButton>
        </div>

        <SideBarHeadline
            :sidebar-headline-config="{
                title: staticKeys?.center_openinghours_headline,
                placeholder: isCenterDetailsPlaceholder
            }"
        />

        <DefaultContentBox :content-box-config="{ type: 'small', placeholder: isCenterDetailsPlaceholder }">
            <component
                :is="SkeletonPlaceholder"
                v-if="isCenterDetailsPlaceholder"
                :skeleton-placeholder-config="{ type: 'text', iterator: 1 }"
            />
            <OpeningHours v-else-if="hasOpeningHours" :opening-hours="centerDetails?.hours?.weekly" />
        </DefaultContentBox>

        <SideBarHeadline
            :sidebar-headline-config="{
                title: staticKeys?.center_route_headline,
                placeholder: isCenterDetailsPlaceholder
            }"
        />

        <component
            :is="SkeletonPlaceholder"
            v-if="isCenterDetailsPlaceholder"
            :skeleton-placeholder-config="{ type: 'button' }"
        />

        <CenterDetailRouteLink v-else-if="centerDetails?.address?.googleMaps" :center-route-link-config="centerRouteConfig" />

        <SideBarHeadline
            :sidebar-headline-config="{
                title: staticKeys?.center_contact_headline,
                placeholder: isCenterDetailsPlaceholder
            }"
        />

        <DefaultContentBox :content-box-config="{ type: 'small', placeholder: isCenterDetailsPlaceholder }">
            <component
                :is="SkeletonPlaceholder"
                v-if="isCenterDetailsPlaceholder"
                :skeleton-placeholder-config="{ type: 'text', iterator: 2 }"
            />
            <CenterDetailContacts v-else-if="hasContacts" :center-contacts="{ ...centerDetails?.contacts }" />
        </DefaultContentBox>

        <DevOnly>
            <pre>{{ centerDetails }}</pre>
        </DevOnly>
    </SideBarWrapper>
</template>

<style lang="scss">
.center-detail-sidebar {
  min-height: 100%;
}

.center-selector-button {
    display: flex;
    justify-content: center;
    padding-block-end: 70px;
}
</style>
